// GenerateModal.jsx
import React, { useEffect, useRef, useState, memo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useLanguage } from "../helpers/LanguageContext";

const rowStyle = {
  display: "flex",
  alignItems: "center",
  padding: "6px 8px",
  borderRadius: "6px",
  cursor: "pointer",
  transition: "background 0.15s ease"
};

const dividerStyle = {
  marginTop: "20px",
  marginBottom: "20px",
  opacity: 0.5
};

const normalizeTrimSize = (value) => {
  const n = Number(value);

  if (!Number.isInteger(n) || n < 0 || n > 9999) {
    return 6;
  }

  return n;
};

function GenerateModal({
  preprocessBeforeGenerate,
  setPreprocessBeforeGenerate,
  show,
  onClose,
  generateScope,
  setGenerateScope,
  generateMode,
  setGenerateMode,
  generateDirection,
  setGenerateDirection,
  allowHalfTable,
  setAllowHalfTable,
  usedColors,
  usedTables,
  selectedColorIds,
  setSelectedColorIds,
  selectedTableIds,
  setSelectedTableIds,
  trimSize,
  setTrimSize,
  onConfirm
}) {
  const { t } = useLanguage();

  const [, forceRender] = useState(0);

  const scopeRef = useRef(generateScope);
  const modeRef = useRef(generateMode);
  const directionRef = useRef(generateDirection || "both");
  const allowHalfTableRef = useRef(Boolean(allowHalfTable));
  const trimRef = useRef(normalizeTrimSize(trimSize));

  const selectedColorsRef = useRef(new Set(selectedColorIds));
  const selectedTablesRef = useRef(new Set(selectedTableIds));

  const pendingConfirmRef = useRef(false);

  useEffect(() => {
    if (!show) return;

    scopeRef.current = generateScope;
    modeRef.current = generateMode;
    directionRef.current = generateDirection || "both";
    allowHalfTableRef.current = Boolean(allowHalfTable);
    trimRef.current = normalizeTrimSize(trimSize);
    selectedColorsRef.current = new Set(selectedColorIds);
    selectedTablesRef.current = new Set(selectedTableIds);

    forceRender((v) => v + 1);
  }, [
    show,
    generateScope,
    generateMode,
    generateDirection,
    allowHalfTable,
    trimSize,
    selectedColorIds,
    selectedTableIds
  ]);

  useEffect(() => {
    if (!pendingConfirmRef.current) return;

    pendingConfirmRef.current = false;
    onConfirm();
  }, [
    generateScope,
    generateMode,
    generateDirection,
    allowHalfTable,
    trimSize,
    selectedColorIds,
    selectedTableIds,
    onConfirm
  ]);

  const selectScope = (value) => {
    scopeRef.current = value;

    if (value === "colors" && usedColors.length > 0) {
      selectedColorsRef.current = new Set(usedColors.map((c) => c.colorId));
    }

    if (value === "tables" && usedTables.length > 0) {
      selectedTablesRef.current = new Set(usedTables.map((tb) => tb.id));
    }

    forceRender((v) => v + 1);
  };

  const toggleColor = (id) => {
    if (selectedColorsRef.current.has(id)) {
      selectedColorsRef.current.delete(id);
    } else {
      selectedColorsRef.current.add(id);
    }

    forceRender((v) => v + 1);
  };

  const toggleTable = (id) => {
    if (selectedTablesRef.current.has(id)) {
      selectedTablesRef.current.delete(id);
    } else {
      selectedTablesRef.current.add(id);
    }

    forceRender((v) => v + 1);
  };

  const confirm = () => {
    pendingConfirmRef.current = true;

    setGenerateScope(scopeRef.current);
    setGenerateMode(modeRef.current);
    setGenerateDirection(directionRef.current);
    setAllowHalfTable(allowHalfTableRef.current);
    setTrimSize(normalizeTrimSize(trimRef.current));
    setSelectedColorIds([...selectedColorsRef.current]);
    setSelectedTableIds([...selectedTablesRef.current]);
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("generateTitle")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Check
          type="switch"
          id="preprocess-before-generate"
          label="Előfeldolgozás elvégzése"
          checked={Boolean(preprocessBeforeGenerate)}
          onChange={(event) => setPreprocessBeforeGenerate(event.target.checked)}
        />
        {preprocessBeforeGenerate && (
          <p className="text-muted small mt-2">
            Az előfeldolgozás a teljes munkára fut. Méret- vagy darabszámváltozás esetén minden tábla újragenerálódik.
          </p>
        )}
        <hr style={dividerStyle} />
        <h6>{t("generateWhat")}</h6>

        <Form>
          {[
            { value: "work", label: t("generateFullWork") },
            ...(usedColors.length > 0
              ? [{ value: "colors", label: t("generateSpecificColors") }]
              : []),
            ...(usedTables.length > 0
              ? [{ value: "tables", label: t("generateSpecificTables") }]
              : [])
          ].map((opt) => (
            <div
              key={opt.value}
              style={rowStyle}
              onClick={() => selectScope(opt.value)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <Form.Check
                type="radio"
                name="generateScope"
                checked={scopeRef.current === opt.value}
                onChange={() => selectScope(opt.value)}
                label={opt.label}
              />
            </div>
          ))}
        </Form>

        <hr style={dividerStyle} />

        <Form.Group className="mb-3">
          <Form.Label>{t("trim")}</Form.Label>
          <Form.Control
            type="number"
            min={0}
            max={9999}
            step={1}
            defaultValue={normalizeTrimSize(trimSize)}
            onChange={(e) => {
              const rawValue = e.target.value;
              const value = Number(rawValue);

              if (rawValue === "") {
                trimRef.current = "";
              } else if (
                Number.isInteger(value) &&
                value >= 0 &&
                value <= 9999
              ) {
                trimRef.current = value;
              } else {
                e.target.value = trimRef.current;
              }
            }}
          />
        </Form.Group>

        <hr style={dividerStyle} />

        <h6>{t("direction")}</h6>

        {[
          { value: "both", label: t("both") },
          { value: "horizontal", label: t("horizontal") },
          { value: "vertical", label: t("vertical") }
        ].map((opt) => (
          <div
            key={opt.value}
            style={rowStyle}
            onClick={() => {
              directionRef.current = opt.value;
              forceRender((v) => v + 1);
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <Form.Check
              type="radio"
              name="generateDirection"
              checked={directionRef.current === opt.value}
              onChange={() => {
                directionRef.current = opt.value;
                forceRender((v) => v + 1);
              }}
              label={opt.label}
            />
          </div>
        ))}

        <hr style={dividerStyle} />

        <h6>{t("halfTable")}</h6>

        {[
          { value: true, label: t("allowHalfTable") },
          { value: false, label: t("disallowHalfTable") }
        ].map((opt) => (
          <div
            key={String(opt.value)}
            style={rowStyle}
            onClick={() => {
              allowHalfTableRef.current = opt.value;
              forceRender((v) => v + 1);
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <Form.Check
              type="radio"
              name="allowHalfTable"
              checked={allowHalfTableRef.current === opt.value}
              onChange={() => {
                allowHalfTableRef.current = opt.value;
                forceRender((v) => v + 1);
              }}
              label={opt.label}
            />
          </div>
        ))}

        {(scopeRef.current === "colors" || scopeRef.current === "tables") && (
          <hr style={dividerStyle} />
        )}
        {scopeRef.current === "colors" &&
          usedColors.length > 0 &&
          usedColors.map((c) => (
            <div
              key={c.colorId}
              style={rowStyle}
              onClick={() => toggleColor(c.colorId)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <Form.Check
                type="checkbox"
                checked={selectedColorsRef.current.has(c.colorId)}
                onChange={() => toggleColor(c.colorId)}
                label={t(c.name)}
              />
            </div>
          ))}

        {scopeRef.current === "tables" &&
          usedTables.length > 0 &&
          usedTables.map((tb) => (
            <div
              key={tb.id}
              style={rowStyle}
              onClick={() => toggleTable(tb.id)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <Form.Check
                type="checkbox"
                checked={selectedTablesRef.current.has(tb.id)}
                onChange={() => toggleTable(tb.id)}
                label={tb.label}
              />
            </div>
          ))}
        {/*
        {(scopeRef.current === "colors" || scopeRef.current === "tables") && (
          <hr style={dividerStyle} />
        )}

        <h6 className="mt-3">{t("generateMode")}</h6>

        {[
          { value: "full", label: t("generateModeFull") }

          // Vonalak újragenerálása ideiglenesen kikommentelve, de megtartva.
          // {
          //   value: "lines",
          //   label: t("generateModeLines")
          // }
        ].map((opt) => {
          const isLines = opt.value === "lines";
          const disabled = isLines && usedTables.length === 0;

          return (
            <div
              key={opt.value}
              style={{
                ...rowStyle,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? "not-allowed" : "pointer"
              }}
              onClick={() => {
                if (!disabled) {
                  modeRef.current = opt.value;
                  forceRender((v) => v + 1);
                }
              }}
              onMouseEnter={(e) => {
                if (!disabled) e.currentTarget.style.background = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <Form.Check
                type="radio"
                name="generateMode"
                checked={modeRef.current === opt.value}
                onChange={() => {
                  if (!disabled) {
                    modeRef.current = opt.value;
                    forceRender((v) => v + 1);
                  }
                }}
                disabled={disabled}
                label={opt.label}
              />
            </div>
          );
        })}*/}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button variant="primary" onClick={confirm}>
          {t("generateBtn")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default memo(GenerateModal);
